<template>
  <AuthCard title="Восстановление пароля" :subtitle="step === 'email' ? 'Укажите email вашего аккаунта' : step === 'code' ? 'Введите код из письма' : 'Введите новый пароль'">
    <form class="recovery-form" @submit.prevent="submit">
      <div v-if="step === 'email'" class="field">
        <label for="password-recovery-email">Email</label>
        <InputText id="password-recovery-email" v-model.trim="email" type="email" autocomplete="email" maxlength="256" required :disabled="loading" />
      </div>
      <template v-else-if="step === 'code'">
        <p>Код отправлен на {{ maskedEmail }}. Он действует 10 минут.</p>
        <div class="field">
          <label for="password-recovery-code">Код из письма</label>
          <InputText id="password-recovery-code" v-model.trim="code" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]{6}" maxlength="6" required :disabled="loading" />
        </div>
        <Button type="button" :label="resendSeconds > 0 ? `Отправить повторно через ${resendSeconds} с` : 'Отправить код повторно'" text :disabled="loading || resendSeconds > 0" @click="sendCode" />
      </template>
      <template v-else>
        <p>Минимум 6 символов, заглавная и строчная буквы, цифра.</p>
        <div class="field">
          <label for="password-recovery-password">Новый пароль</label>
          <Password inputId="password-recovery-password" v-model="password" autocomplete="new-password" :feedback="false" toggleMask :disabled="loading" />
        </div>
        <div class="field">
          <label for="password-recovery-confirm">Подтвердите пароль</label>
          <Password inputId="password-recovery-confirm" v-model="confirmPassword" autocomplete="new-password" :feedback="false" toggleMask :disabled="loading" />
        </div>
      </template>
      <p v-if="error" role="alert" class="field-error">{{ error }}</p>
      <Button class="primary-btn" type="submit" :label="step === 'email' ? 'Отправить код' : step === 'code' ? 'Подтвердить код' : 'Изменить пароль'" :loading="loading" :disabled="loading" />
      <Button v-if="step !== 'email'" type="button" label="Указать другой email" severity="secondary" outlined :disabled="loading" @click="changeEmail" />
      <Button type="button" label="Вернуться ко входу" text :disabled="loading" @click="emit('back')" />
    </form>
  </AuthCard>
</template>

<script setup>
import { ref, onUnmounted } from 'vue'
import AuthCard from '@/shared/ui/AuthCard.vue'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Button from 'primevue/button'
import { useAppToast } from '@/shared/lib/useAppToast'
import { startPasswordRecovery, verifyPasswordRecovery, resetRecoveredPassword } from '@/modules/auth/api/auth.service'

const emit = defineEmits(['back'])
const toast = useAppToast()
const step = ref('email')
const email = ref('')
const maskedEmail = ref('')
const code = ref('')
const resetToken = ref('') // Memory only; never put recovery credentials in URLs or browser storage.
const password = ref('')
const confirmPassword = ref('')
const error = ref('')
const loading = ref(false)
const resendSeconds = ref(0)
let timer
onUnmounted(() => { clearInterval(timer) })
function cooldown(seconds) {
  clearInterval(timer)
  resendSeconds.value = Math.max(0, Number(seconds) || 60)
  timer = setInterval(() => {
    resendSeconds.value = Math.max(0, resendSeconds.value - 1)
    if (!resendSeconds.value) clearInterval(timer)
  }, 1000)
}
function changeEmail() {
  step.value = 'email'
  email.value = ''
  code.value = ''
  resetToken.value = ''
  password.value = ''
  confirmPassword.value = ''
  error.value = ''
}
async function run(action) {
  if (loading.value) return
  loading.value = true
  error.value = ''
  try { await action() } catch (e) {
    error.value = Object.values(e.fieldErrors || {}).flat().join(' ') || e.message || 'Не удалось выполнить запрос. Попробуйте позже.'
    if (e.status === 429) cooldown(e.retryAfterSeconds || 60)
  } finally { loading.value = false }
}
async function sendCode() {
  if (step.value === 'code' && resendSeconds.value > 0) return
  if (!email.value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
    error.value = 'Введите корректный email'
    return
  }
  await run(async () => {
    const { data } = await startPasswordRecovery(email.value)
    maskedEmail.value = data.maskedEmail
    code.value = ''
    resetToken.value = ''
    step.value = 'code'
    cooldown(data.resendAfterSeconds)
  })
}
async function submit() {
  if (step.value === 'email') return sendCode()
  if (step.value === 'code') {
    if (!/^[0-9]{6}$/.test(code.value)) { error.value = 'Введите код из 6 цифр'; return }
    return run(async () => {
      const { data } = await verifyPasswordRecovery(email.value, code.value)
      resetToken.value = data.resetToken
      code.value = ''
      step.value = 'password'
    })
  }
  if (password.value !== confirmPassword.value) { error.value = 'Пароли не совпадают'; return }
  if (password.value.length < 6 || !/[A-Z]/.test(password.value) || !/[a-z]/.test(password.value) || !/[0-9]/.test(password.value)) {
    error.value = 'Пароль должен содержать минимум 6 символов, заглавную и строчную буквы, цифру'
    return
  }
  await run(async () => {
    await resetRecoveredPassword({ email: email.value, resetToken: resetToken.value, password: password.value, confirmPassword: confirmPassword.value })
    changeEmail()
    toast.success('Пароль успешно изменён. Войдите с новым паролем.')
    emit('back')
  })
}
</script>

<style scoped>
.recovery-form, .field { display: flex; flex-direction: column; gap: 10px; }
label, p { color: var(--muted); font-size: 13px; }
.field-error { color: #dc2626; }
:deep(.p-inputtext), :deep(.p-password-input) { width: 100%; border-color: var(--border); background: var(--input-bg); }
</style>
